
import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { questionBankAPI } from "@/services/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Package, Search, Check, ShoppingCart } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const QuestionBankPurchase = () => {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBank, setSelectedBank] = useState(null);
  const [isPurchaseModalOpen, setIsPurchaseModalOpen] = useState(false);

  const { data: availableBanks = [], isLoading } = useQuery({
    queryKey: ["questionBanks"],
    queryFn: () => questionBankAPI.getAll(),
    select: (res) => res.data.filter(bank => 
      bank.status === 'published' && 
      !bank.name.toLowerCase().includes('ai generated') &&
      !bank.name.toLowerCase().includes('ai-generated') &&
      !bank.isAIGenerated
    ),
  });

  const { data: purchasedBanks = [], isLoading: isLoadingPurchased } = useQuery({
    queryKey: ["purchasedQuestionBanks"],
    queryFn: () => questionBankAPI.getPurchased(),
    select: (res) => res.data.filter(bank => 
      !bank.name.toLowerCase().includes('ai generated') &&
      !bank.name.toLowerCase().includes('ai-generated') &&
      !bank.isAIGenerated
    ),
  });

  const purchaseMutation = useMutation({
    mutationFn: (bankId) => questionBankAPI.purchase({ bankId }),
    onSuccess: () => {
      toast.success("Question bank purchased successfully!");
      queryClient.invalidateQueries({ queryKey: ["purchasedQuestionBanks"] });
      setIsPurchaseModalOpen(false);
    },
    onError: (error) => {
      toast.error(
        error.response?.data?.message || "Failed to purchase question bank. Please try again."
      );
    },
  });

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handlePurchase = () => {
    purchaseMutation.mutate(selectedBank._id);
  };

  const openPurchaseModal = (bank) => {
    setSelectedBank(bank);
    setIsPurchaseModalOpen(true);
  };

  const filteredBanks = availableBanks.filter((bank) =>
    bank.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bank.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
    bank.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-200px)]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const isPurchased = (bankId) => {
    return purchasedBanks.some(bank => bank._id === bankId);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Question Banks</h1>
        <p className="text-muted-foreground">
          Browse and purchase question banks for your exams
        </p>
      </div>

      <Tabs defaultValue="available" className="space-y-4">
        <TabsList>
          <TabsTrigger value="available">Available Banks</TabsTrigger>
          <TabsTrigger value="purchased">Purchased Banks</TabsTrigger>
        </TabsList>

        <TabsContent value="available">
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name, category or description..."
                className="pl-10"
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>

          {filteredBanks.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No question banks found</p>
              <p className="text-muted-foreground">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredBanks.map((bank) => {
                const alreadyPurchased = isPurchased(bank._id);
                
                return (
                  <Card key={bank._id} className={alreadyPurchased ? "border-green-400" : ""}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <CardTitle>{bank.name}</CardTitle>
                        <Badge variant={alreadyPurchased ? "outline" : "secondary"}>
                          {alreadyPurchased ? (
                            <Check className="h-3 w-3 mr-1" />
                          ) : null}
                          {alreadyPurchased ? "Purchased" : bank.category}
                        </Badge>
                      </div>
                      <CardDescription className="line-clamp-2">
                        {bank.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex justify-between text-sm mb-4">
                        <span className="text-muted-foreground">Questions:</span>
                        <span className="font-medium">{bank.questionCount}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Price:</span>
                        <span className="font-medium">${bank.price}</span>
                      </div>
                    </CardContent>
                    <CardFooter>
                      <Button 
                        className="w-full" 
                        variant={alreadyPurchased ? "outline" : "default"}
                        disabled={alreadyPurchased}
                        onClick={() => openPurchaseModal(bank)}
                      >
                        {alreadyPurchased ? (
                          <Check className="h-4 w-4 mr-2" />
                        ) : (
                          <ShoppingCart className="h-4 w-4 mr-2" />
                        )}
                        {alreadyPurchased ? "Already Purchased" : "Purchase"}
                      </Button>
                    </CardFooter>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="purchased">
          {isLoadingPurchased ? (
            <div className="flex items-center justify-center h-60">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : purchasedBanks.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 rounded-lg border-2 border-dashed">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-lg font-medium">No purchased question banks</p>
              <p className="text-muted-foreground">
                Purchase question banks to use them in your exams
              </p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => document.querySelector('[data-value="available"]').click()}
              >
                Browse Available Banks
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {purchasedBanks.map((bank) => (
                <Card key={bank._id} className="border-green-400">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle>{bank.name}</CardTitle>
                      <Badge variant="outline">
                        <Check className="h-3 w-3 mr-1" />
                        Purchased
                      </Badge>
                    </div>
                    <CardDescription className="line-clamp-2">
                      {bank.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Questions:</span>
                      <span className="font-medium">{bank.questionCount}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-muted-foreground">Purchase Date:</span>
                      <span className="font-medium">
                        {new Date(bank.purchasedAt).toLocaleDateString()}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Price Paid:</span>
                      <span className="font-medium">${bank.purchasePrice || bank.price}</span>
                    </div>
                  </CardContent>
                  <CardFooter>
                    <Button className="w-full" variant="secondary">
                      Use in Exams
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Purchase Confirmation Modal */}
      <Dialog open={isPurchaseModalOpen} onOpenChange={setIsPurchaseModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Purchase</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {selectedBank && (
              <>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="font-medium">{selectedBank.name}</h3>
                    <p className="text-sm text-muted-foreground">
                      {selectedBank.description}
                    </p>
                  </div>
                  <Badge>{selectedBank.category}</Badge>
                </div>
                <div className="flex justify-between text-sm mb-2">
                  <span>Questions:</span>
                  <span className="font-medium">{selectedBank.questionCount}</span>
                </div>
                <div className="flex justify-between text-sm border-t pt-4 mt-4">
                  <span className="font-medium">Total Price:</span>
                  <span className="font-bold text-lg">${selectedBank.price}</span>
                </div>
                <p className="text-sm text-muted-foreground mt-4">
                  By purchasing this question bank, you will have access to all questions included and can use them in your exams.
                </p>
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsPurchaseModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePurchase}
              disabled={purchaseMutation.isPending}
            >
              {purchaseMutation.isPending ? "Processing..." : "Confirm Purchase"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionBankPurchase;
